/* paty-cart.js — recomendaciones del carrito (drawer). CSP-safe, sin handlers inline.
 *
 * DOS bloques separados (2026-08-14):
 *  1. «Comprados juntos» (`paty-cart-cross`, `intent=complementary`): los que el
 *     usuario configura a mano en Search & Discovery. Va en banda FIJA entre la
 *     lista y el resumen — antes todo vivía al final de la zona scrolleable y en
 *     móvil no lo veía nadie, que es justo donde se pierden los cross-sell.
 *  2. «También te puede gustar» (`paty-cart-recos`, `intent=related`): el
 *     algoritmo de Shopify, dentro del scroll, bajo los productos. Se le quitan
 *     los que ya salen arriba para no repetir producto.
 *
 * ⚠️ `cart.items[0]` es el ÚLTIMO producto añadido, no el primero: Shopify mete
 * las líneas nuevas al PRINCIPIO del carrito (verificado dos veces en la tienda:
 * al añadir un tercer producto entró en la posición 0). Por eso el array se
 * recorre tal cual, sin invertir.
 */
(() => {
  const RECOS_SECTION = 'paty-cart-recos';
  const CROSS_SECTION = 'paty-cart-cross';
  // Con prefijo de idioma (/en/…) para que las secciones se rendericen en el idioma activo.
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const RECOS_URL = `${ROOT}recommendations/products`;
  const MAX_CARDS = 6;   // el drawer es estrecho: más tarjetas es scroll infinito
  const MAX_SEEDS = 3;   // como mucho 3 productos semilla para los complementarios
  let lastKey = '';
  let busy = false;

  const drawer = () => document.getElementById('cart-drawer');

  // Lee el carrito actual (handles + product_ids) vía /cart.js
  async function readCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const cart = await res.json();
      if (!cart.items || cart.items.length === 0) return null;
      const handles = new Set(cart.items.map((i) => i.handle));
      const ids = [...new Set(cart.items.map((i) => i.product_id))];
      return { latestId: ids[0], ids, handles };
    } catch {
      return null;
    }
  }

  // Un render de sección por Section Rendering API → fragmento de DOM.
  async function fetchSection(sectionId, productId, intent, limit) {
    try {
      const url = `${RECOS_URL}?section_id=${sectionId}&product_id=${productId}&intent=${intent}&limit=${limit}`;
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return null;
      const html = (await res.text()).trim();
      if (!html) return null;
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      return tpl.content;
    } catch {
      return null;
    }
  }

  // Banda fija: hermana del resumen y justo antes que él.
  function crossHost() {
    const d = drawer();
    if (!d) return null;
    let host = d.querySelector('[data-paty-cross-host]');
    if (host) return host;
    const summary = d.querySelector('.cart-drawer__summary');
    if (!summary || !summary.parentElement) return null;
    host = document.createElement('div');
    host.setAttribute('data-paty-cross-host', '');
    summary.parentElement.insertBefore(host, summary);
    watchSummary(summary);
    return host;
  }

  // Relacionados: al final de la lista de items (zona scrolleable), como siempre.
  function recosHost() {
    const d = drawer();
    if (!d) return null;
    let host = d.querySelector('[data-paty-recos-host]');
    if (host) return host;
    const list = d.querySelector('.cart-drawer__items') || d.querySelector('.cart-drawer__content');
    if (!list) return null;
    host = document.createElement('div');
    host.setAttribute('data-paty-recos-host', '');
    list.appendChild(host);
    return host;
  }

  // La banda es sticky por encima del resumen, y el alto del resumen cambia
  // (aparece el descuento, el ahorro, la nota…). Se mide en vivo y se publica
  // como `--paty-cross-bottom` en vez de cablear un número en el CSS.
  let summaryRO = null;
  function watchSummary(summary) {
    const apply = () => {
      const d = drawer();
      if (d) d.style.setProperty('--paty-cross-bottom', Math.round(summary.offsetHeight) + 'px');
    };
    apply();
    if (typeof ResizeObserver === 'function') {
      if (summaryRO) summaryRO.disconnect();
      summaryRO = new ResizeObserver(apply);
      summaryRO.observe(summary);
    }
  }

  // Saca las tarjetas de los fragmentos en orden, sin repetir ni colar lo que ya
  // está en el carrito. `skip` sirve para que los relacionados no repitan lo que
  // ya enseña la banda de arriba.
  function pickItems(fragments, cartHandles, skip) {
    const seen = new Set();
    const items = [];
    for (const fragment of fragments) {
      fragment.querySelectorAll('[data-product-handle]').forEach((el) => {
        const handle = el.getAttribute('data-product-handle');
        if (!handle || seen.has(handle) || cartHandles.has(handle) || (skip && skip.has(handle))) return;
        seen.add(handle);
        items.push(el);
      });
    }
    return items;
  }

  // ⚠️ Mover las elegidas ANTES de vaciar el contenedor destino: parte de ellas
  // cuelgan de ese mismo contenedor (el del fragmento que aporta el shell), así
  // que vaciarlo primero las destruiría.
  function fill(container, items) {
    const ordered = document.createDocumentFragment();
    items.forEach((el) => ordered.appendChild(el));
    container.textContent = '';
    container.appendChild(ordered);
  }

  async function loadRecos() {
    if (busy) return;
    const d = drawer();
    if (!d) return;
    const info = await readCart();
    const cross = crossHost();
    const recos = recosHost();
    if (!cross && !recos) return;
    if (!info) {
      if (cross) cross.innerHTML = '';
      if (recos) recos.innerHTML = '';
      lastKey = '';
      return;
    }
    const key = String(info.latestId) + ':' + [...info.handles].sort().join(',');
    const painted = (cross && cross.innerHTML.trim() !== '') || (recos && recos.innerHTML.trim() !== '');
    if (key === lastKey && painted) return; // ya cargado para este estado
    busy = true;
    try {
      // Semillas de complementarios: el carrito ya viene con el último añadido
      // delante, así que se recorre tal cual. Los relacionados siguen saliendo
      // de esa misma cabeza del carrito, como hasta ahora.
      const seeds = info.ids.slice(0, MAX_SEEDS);
      const [complementary, related] = await Promise.all([
        Promise.all(seeds.map((id) => fetchSection(CROSS_SECTION, id, 'complementary', 4))),
        fetchSection(RECOS_SECTION, info.latestId, 'related', 8),
      ]);

      // 1. Banda fija de complementarios.
      const crossFragments = complementary.filter(Boolean);
      const crossItems = pickItems(crossFragments, info.handles).slice(0, MAX_CARDS);
      // ⚠️ El shell hay que buscarlo en el PRIMER fragmento QUE LO TENGA, no en
      // el primero a secas: un producto sin complementarios no devuelve vacío,
      // devuelve ~73 caracteres (los comentarios con los que Shopify envuelve la
      // sección), así que el fragmento existe pero no trae `.paty-cross`.
      let crossShell = null;
      for (const fragment of crossFragments) {
        crossShell = fragment.querySelector('.paty-cross');
        if (crossShell) break;
      }
      const crossTrack = crossShell && crossShell.querySelector('[data-paty-cross-track]');
      if (cross) {
        if (crossShell && crossTrack && crossItems.length) {
          fill(crossTrack, crossItems);
          cross.innerHTML = '';
          cross.appendChild(crossShell);
          watchTrack(crossShell);
        } else {
          cross.innerHTML = '';   // sin complementarios no se pinta nada: ni título huérfano ni hueco
        }
      }

      // 2. Rejilla de relacionados, sin repetir lo que ya está arriba.
      const shownAbove = new Set(crossItems.map((el) => el.getAttribute('data-product-handle')));
      const recosShell = related ? related.querySelector('.paty-cart-recos') : null;
      const recosGrid = recosShell && recosShell.querySelector('.paty-grid');
      if (recos) {
        const recosItems = related ? pickItems([related], info.handles, shownAbove).slice(0, MAX_CARDS) : [];
        if (recosShell && recosGrid && recosItems.length) {
          fill(recosGrid, recosItems);
          recos.innerHTML = '';
          recos.appendChild(recosShell);
        } else {
          recos.innerHTML = '';
        }
      }

      lastKey = key;
    } catch {
      if (cross) cross.innerHTML = '';
      if (recos) recos.innerHTML = '';
    } finally {
      busy = false;
    }
  }

  /* ---------- Carrusel de la banda (una tarjeta por «página») ---------- */

  // ⚠️ Medir el track NADA MÁS insertarlo no vale: cuando `loadRecos` corre, el
  // drawer puede estar todavía sin dimensiones (ancho 0) y entonces
  // scrollWidth == clientWidth == 0, o sea "no desborda" → las flechas se
  // quedaban ocultas para siempre. Con ResizeObserver se recalcula en cuanto la
  // caja tiene tamaño de verdad (y también al girar el móvil).
  let trackRO = null;
  function watchTrack(root) {
    const track = root.querySelector('[data-paty-cross-track]');
    if (!track) return;
    syncCrossNav(root);
    if (typeof ResizeObserver === 'function') {
      if (trackRO) trackRO.disconnect();
      trackRO = new ResizeObserver(() => syncCrossNav(root));
      trackRO.observe(track);
    } else {
      setTimeout(() => syncCrossNav(root), 400);
    }
  }

  // Las flechas solo existen si hay algo que desplazar, y se apagan en los topes.
  function syncCrossNav(root) {
    const track = root.querySelector('[data-paty-cross-track]');
    const nav = root.querySelector('[data-paty-cross-nav]');
    if (!track || !nav) return;
    const overflow = track.scrollWidth - track.clientWidth;
    if (overflow < 2) { nav.hidden = true; return; }
    nav.hidden = false;
    const prev = nav.querySelector('[data-paty-cross-dir="prev"]');
    const next = nav.querySelector('[data-paty-cross-dir="next"]');
    if (prev) prev.disabled = track.scrollLeft < 2;
    if (next) next.disabled = track.scrollLeft > overflow - 2;
  }

  document.addEventListener('click', (e) => {
    const arrow = e.target.closest('[data-paty-cross-dir]');
    if (!arrow) return;
    e.preventDefault();
    const root = arrow.closest('[data-paty-cross]');
    const track = root && root.querySelector('[data-paty-cross-track]');
    if (!track) return;
    const step = track.clientWidth || 240;
    track.scrollBy({
      left: arrow.getAttribute('data-paty-cross-dir') === 'prev' ? -step : step,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  });

  // El swipe también manda: tras deslizar hay que repintar el estado de las flechas.
  document.addEventListener('scroll', (e) => {
    const track = e.target;
    if (!track || !track.hasAttribute || !track.hasAttribute('data-paty-cross-track')) return;
    const root = track.closest('[data-paty-cross]');
    if (root) syncCrossNav(root);
  }, true);

  /* ---------- Disparadores ---------- */

  // Apertura del drawer (click en el icono) y cambios de carrito.
  document.addEventListener('click', (e) => {
    if (e.target.closest('[on\\:click*="cart-drawer"], a[href$="/cart"], .header-actions__cart-icon, cart-icon-component')) {
      setTimeout(loadRecos, 250);
    }
  }, true);

  // El drawer de Horizon emite eventos al actualizarse el carrito; reintentamos al detectar
  // mutaciones en el inner del drawer (re-render por SRA) — robusto y desacoplado.
  const mo = new MutationObserver(() => {
    const d = drawer();
    if (d && d.querySelector('.cart-drawer__inner:not(.cart-drawer--empty)')) {
      clearTimeout(mo._t);
      mo._t = setTimeout(loadRecos, 300);
    }
  });
  function observe() {
    const d = drawer();
    if (d) mo.observe(d, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
